import { React, useState, useEffect } from "react";
import PageLoader from "../loader/PageLoader";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import { Box, Grid, Paper } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CardActionArea from "@mui/material/CardActionArea";
import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";

const Home = () => {
  const [isFetchingDashboardData, setIsFetchingDashboardData] = useState(true);
  const [refreshDashboardData, setRefreshDashboardData] = useState(null);

  // Fetch dashboard data
  const [dailyMonthTenants, setDailyMonthTenants] = useState([]);

  const [totalTenantManagers, setTotalTenantManagers] = useState(0);
  const [totalTenants, setTotalTenants] = useState(0);
  const [totalBusinessManagers, setTotalBusinessManagers] = useState(0);
  const [totalTenantWorkers, setTotalTenantWorkers] = useState(0);
  const [totalPaymentModes, setTotalPaymentModes] = useState(0);
  const [totalLocations, setTotalLocations] = useState(0);
  const [totalSpecialities, setTotalSpecialities] = useState(0);
  const [totalJobs, setTotalJobs] = useState(0);

  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const lastPaperStyle = {
    padding: 20,
    height: "45vh",
    width: 1220,
    margin: "20px auto",
  };

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  useEffect(() => {
    if (!isFetchingDashboardData) {
      setIsFetchingDashboardData(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getMonthlyRevenueBarChartData = async () => {
      try {
        const DASHBOARD_URL = "api/v1/business-dashboard";
        const response = await axiosPrivate.get(
          DASHBOARD_URL,
          {
            signal: controller.signal,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        //const cUser = JSON.stringify(response.data.data, null, 4);

        isMounted && setDailyMonthTenants(response.data.data.dailyMonthTenants);
        isMounted &&
          setTotalTenantManagers(response.data.data.totalTenantManagers);
        isMounted && setTotalTenants(response.data.data.totalTenants);
        isMounted &&
          setTotalBusinessManagers(response.data.data.totalBusinessManagers);
        isMounted &&
          setTotalTenantWorkers(response.data.data.totalTenantWorkers);
        isMounted && setTotalPaymentModes(response.data.data.totalPaymentModes);
        isMounted && setTotalLocations(response.data.data.totalLocations);
        isMounted && setTotalSpecialities(response.data.data.totalSpecialities);
        isMounted && setTotalJobs(response.data.data.totalJobs);

        setIsFetchingDashboardData(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingDashboardData(false);
      }
    };

    getMonthlyRevenueBarChartData();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshDashboardData]);

  return isFetchingDashboardData ? (
    <PageLoader title="Loading dashboard..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      {dailyMonthTenants?.length ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <Grid>
            <Box height={30} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Tenants
                        </Typography>
                        <Typography variant="h5" component="div">
                          {totalTenants}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Registered So Far
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Tenant Admins
                        </Typography>
                        <Typography variant="h6" component="div">
                          {totalTenantManagers}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Added So Far
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Tenant Workers
                        </Typography>
                        <Typography variant="h6" component="div">
                          {totalTenantWorkers}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Added So Far
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Business Admins
                        </Typography>
                        <Typography variant="h6" component="div">
                          {totalBusinessManagers}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Added So Far
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={3}
              sx={{
                mt: 0.1,
              }}
            >
              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Tenant Jobs
                        </Typography>
                        <Typography variant="h5" component="div">
                          {totalJobs}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Created So Far
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Payment Modes
                        </Typography>
                        <Typography variant="h6" component="div">
                          {totalPaymentModes}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Currently Supported
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Specialities
                        </Typography>
                        <Typography variant="h6" component="div">
                          {totalSpecialities}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Currently Supported
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Box mb={1.5}>
                  <Card>
                    <CardActionArea
                      //onClick={() => setSelectedCard(index)}
                      //data-active={selectedCard === index ? "" : undefined}
                      sx={{
                        height: "100%",
                        "&[data-active]": {
                          backgroundColor: "action.selected",
                          "&:hover": {
                            backgroundColor: "action.selectedHover",
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Locations
                        </Typography>
                        <Typography variant="h6" component="div">
                          {totalLocations}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Currently Supported
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={3}>
                <Paper elevation={10} style={lastPaperStyle}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      width={500}
                      height={300}
                      data={dailyMonthTenants}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="tenants"
                        fill="#8884d8"
                        stackId="stack"
                        activeBar={<Rectangle fill="pink" stroke="blue" />}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No dashboard data to display
          </Typography>
        </div>
      )}
    </Box>
  );
};

export default Home;
